import { Loader2 } from "lucide-react";
import type { MouseEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui";
import { useEvidenceSetExport } from "~/features/dataImportProjects/hooks/useEvidenceSetExport";

interface EvidenceSetExportDialogProps {
  evidenceSetId: string;
  open: boolean;
  onClose: () => void;
}

export function EvidenceSetExportDialog({ open, onClose, evidenceSetId }: EvidenceSetExportDialogProps) {
  const { t } = useTranslation();
  const { onExport, exporting } = useEvidenceSetExport({ onClose, evidenceSetId });

  // Radix closes on action click; hold it open so the spinner is visible until `onExport` settles.
  const handleExport = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (exporting) return;
    await onExport();
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !exporting) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="capitalize">{t("feature.evidenceSets.exportDialog.title")}</AlertDialogTitle>
          <AlertDialogDescription>{t("feature.evidenceSets.exportDialog.explanatoryText")}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={exporting}>{t("ui.cancel")}</AlertDialogCancel>
          <AlertDialogAction disabled={exporting} onClick={handleExport}>
            {exporting && <Loader2 className="animate-spin" />}
            <span>{t("ui.export")}</span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
